// src/pages/portal/reporting/PortalEodPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PortalLayout from '../PortalLayout';
import { sessionService, eodReportService } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileHero from '../../../components/common/MobileHero';
import MobileBanner from '../../../components/common/MobileBanner';
import BusinessDateCalendar from '../../../components/portal/BusinessDateCalendar';
import { C } from '../../../styles/theme';
import {
    FONT,
    fmt,
    formatDisplayDate,
    Modal,
    KpiCard,
    SectionHeader,
    ActiveSessionCard,
    ClosedSessionCard,
    StoreSummaryCard,
    EmptyState,
} from './eod/EodCashSections';
import EodSalesSections from './eod/EodSalesSections';
import EodExportMenu from './eod/EodExportMenu';


// ─── Main Page ────────────────────────────────────────────────────────────────
const PortalEodPage = () => {
    const { getCurrency, getTenantSetting } = useAuth();
    const { t } = useLanguage();
    const isMobile = useIsMobile();
    const currency = getCurrency();
    const eodMode = getTenantSetting('eod_mode', 'per_store');

    const [selectedDate, setSelectedDate] = useState('');
    const [viewMode, setViewMode] = useState(eodMode === 'per_till' ? 'per_till' : 'store_summary');
    const [businessDates, setBusinessDates] = useState([]);
    const [datesLoaded, setDatesLoaded] = useState(false);
    const availableDates = useMemo(() => new Set(businessDates), [businessDates]);
    const [sessions, setSessions] = useState([]);
    const [storeSummary, setStoreSummary] = useState(null);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);

    // Accordion state for sales_date groups
    const [expandedGroups, setExpandedGroups] = useState({});
    const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

    // Force-close modal state
    const [showForceClose, setShowForceClose] = useState(null);
    const [forceCloseReason, setForceCloseReason] = useState('');
    const [forceCloseNotCounted, setForceCloseNotCounted] = useState(false);
    const [forceClosing, setForceClosing] = useState(false);

    // ─── Derived data ─────────────────────────────────────────────────────────
    const activeSessions = sessions.filter(s => s.status === 'open');
    const closedSessions = sessions.filter(s => s.status !== 'open');
    const flaggedCount = closedSessions.filter(s => s.is_flagged || s.flagged).length;

    // Group all sessions by sales_date
    const sessionsBySalesDate = useMemo(() => {
        const groups = {};
        sessions.forEach(s => {
            const key = s.sales_date || 'unknown';
            if (!groups[key]) groups[key] = [];
            groups[key].push(s);
        });
        // Sort keys descending (most recent first)
        return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
    }, [sessions]);

    // ─── Business-date loading ─────────────────────────────────────────────────
    const loadBusinessDates = useCallback(async (selectMostRecent = false) => {
        try {
            const dates = await sessionService.getBusinessDates();
            const list = Array.isArray(dates) ? dates : [];
            setBusinessDates(list);
            setDatesLoaded(true);
            if (selectMostRecent) {
                setSelectedDate(list.length > 0 ? list[0] : '');
            }
        } catch (err) {
            console.error('Failed to load business dates:', err);
            setBusinessDates([]);
            setDatesLoaded(true);
            if (selectMostRecent) setSelectedDate('');
        }
    }, []);

    useEffect(() => { loadBusinessDates(true); }, [loadBusinessDates]);

    // Keep the active tab aligned with the tenant's mode — covers the case where
    // tenantConfig (and thus eodMode) resolves after the initial render.
    useEffect(() => {
        setViewMode(eodMode === 'per_till' ? 'per_till' : 'store_summary');
    }, [eodMode]);

    // ─── Data loading ─────────────────────────────────────────────────────────
    const loadData = useCallback(async () => {
        if (!selectedDate) {
            setSessions([]);
            setStoreSummary(null);
            setReport(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        // Drop the previous day's report before fetching the new one. The top
        // bar (and its Export control) renders outside the loading branch, so
        // leaving the old payload in place would let a manager export Aug 25's
        // figures while the calendar already reads Aug 26. Clearing it disables
        // Export for the duration of the fetch.
        setReport(null);
        try {
            // The report is fetched alongside the sessions so the sales half and
            // the cash half of the page always describe the same business day.
            // allSettled, not all: a failed report must not blank the cash
            // reconciliation, which is the page's original job.
            const [sessionsRes, summaryRes, reportRes] = await Promise.allSettled([
                sessionService.getSessionsByDate(selectedDate),
                sessionService.getStoreSummary(selectedDate),
                eodReportService.getReport(selectedDate),
            ]);
            const sessionsList = sessionsRes.status === 'fulfilled' && Array.isArray(sessionsRes.value)
                ? sessionsRes.value : [];
            setSessions(sessionsList);
            setStoreSummary(summaryRes.status === 'fulfilled' ? (summaryRes.value || null) : null);
            if (reportRes.status === 'fulfilled') {
                setReport(reportRes.value || null);
            } else {
                console.error('Failed to load EOD report:', reportRes.reason);
                setReport(null);
            }
            // Auto-expand all sales_date groups
            const groups = {};
            sessionsList.forEach(s => { if (s.sales_date) groups[s.sales_date] = true; });
            setExpandedGroups(groups);
        } catch (err) {
            console.error('Failed to load EOD data:', err);
            setSessions([]);
            setStoreSummary(null);
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [selectedDate]);

    useEffect(() => { loadData(); }, [loadData]);

    // ─── Force close handler ──────────────────────────────────────────────────
    const handleForceClose = async () => {
        if (!showForceClose) return;
        setForceClosing(true);
        try {
            await sessionService.forceCloseSession(showForceClose.session_id, {
                reason: forceCloseReason,
                not_counted: forceCloseNotCounted,
            });
            setShowForceClose(null);
            setForceCloseReason('');
            setForceCloseNotCounted(false);
            await Promise.all([loadData(), loadBusinessDates()]);
        } catch (err) {
            console.error('Force close failed:', err);
        } finally {
            setForceClosing(false);
        }
    };

    // ─── KPI computation ──────────────────────────────────────────────────────
    const totalSales = storeSummary?.total_sales ?? sessions.reduce((s, x) => s + (x.total_sales ?? 0), 0);
    const totalCash = storeSummary?.total_cash ?? sessions.reduce((s, x) => s + (x.cash_sales ?? 0), 0);
    const totalExpenses = storeSummary?.total_expenses ?? sessions.reduce((s, x) => s + (x.total_expenses ?? 0), 0);
    const cashPct = totalSales > 0 ? Math.round((totalCash / totalSales) * 100) : 0;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <PortalLayout>
            {/* Top bar */}
            <div style={{
                background: C.white, borderBottom: `1px solid ${C.border}`,
                padding: isMobile ? 16 : '16px 32px', display: 'flex',
                alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between',
                flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0,
                fontFamily: FONT,
            }}>
                {!isMobile && (
                    <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>
                        {t('end_of_day') || 'End of Day'}
                    </div>
                )}
                <div style={{
                    display: 'flex', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 10 : 16,
                    flexDirection: isMobile ? 'column' : 'row',
                }}>
                    {/* View toggle — only when tenant runs per-till EOD */}
                    {eodMode === 'per_till' && (
                        <div className={isMobile ? 'portal-fill' : undefined} style={{
                            display: 'flex', background: '#f1f5f9', borderRadius: 8,
                            overflow: 'hidden', border: `1px solid ${C.border}`,
                            width: isMobile ? '100%' : 'auto',
                        }}>
                            {['per_till', 'store_summary'].map(mode => (
                                <button
                                    key={mode}
                                    onClick={() => setViewMode(mode)}
                                    style={{
                                        padding: '8px 16px', fontSize: 12, fontWeight: 600,
                                        cursor: 'pointer', border: 'none',
                                        background: viewMode === mode ? C.green : 'transparent',
                                        color: viewMode === mode ? C.white : C.muted,
                                        transition: 'all 0.15s', fontFamily: FONT,
                                    }}
                                >
                                    {mode === 'per_till' ? (t('per_till') || 'Per Till') : (t('store_summary') || 'Store Summary')}
                                </button>
                            ))}
                        </div>
                    )}
                    {/* Date picker — business-date calendar */}
                    <BusinessDateCalendar
                        value={selectedDate || null}
                        availableDates={availableDates}
                        onChange={setSelectedDate}
                    />
                    <EodExportMenu report={report} t={t} isMobile={isMobile} />
                </div>
            </div>

            {/* Content */}
            <div style={{ padding: isMobile ? 16 : '24px 32px' }}>
                {!datesLoaded ? (
                    <div style={{ textAlign: 'center', padding: 60, fontFamily: FONT, color: C.muted }}>
                        {t('loading') || 'Loading...'}
                    </div>
                ) : businessDates.length === 0 ? (
                    <EmptyState
                        t={t}
                        title={t('eod_no_business_days') || 'No business days yet'}
                        message={t('eod_no_business_days_desc') || 'Start a business day in the POS and it will appear here.'}
                    />
                ) : loading ? (
                    <div style={{ textAlign: 'center', padding: 60, fontFamily: FONT, color: C.muted }}>
                        {t('loading') || 'Loading...'}
                    </div>
                ) : (sessions.length === 0 && !storeSummary) ? (
                    <EmptyState t={t} />
                ) : (
                    <>
                        {/* Summary — mobile: hero + reconciliation banner; desktop: KPI grid */}
                        {isMobile ? (
                            <>
                                <MobileHero
                                    label={t('total_sales') || 'Total Sales'}
                                    value={fmt(totalSales, currency)}
                                    meta={`${sessions.length} ${sessions.length !== 1 ? (t('tills') || 'tills') : (t('till') || 'till')} · ${formatDisplayDate(selectedDate)}`}
                                    stats={[
                                        { label: t('cash') || 'Cash', value: fmt(totalCash, currency) },
                                        { label: t('total_expenses') || 'Expenses', value: fmt(totalExpenses, currency) },
                                        { label: t('net_takings') || 'Net', value: fmt(totalSales - totalExpenses, currency) },
                                    ]}
                                />
                                {(() => {
                                    const totalDiff = closedSessions.reduce((s, x) => s + (x.cash_difference ?? 0), 0);
                                    if (flaggedCount > 0) {
                                        return (
                                            <MobileBanner
                                                tone="bad"
                                                icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>}
                                                title={`${flaggedCount} ${flaggedCount !== 1 ? (t('tills') || 'tills') : (t('till') || 'till')} ${t('need_review') || 'need review'}`}
                                                subtitle={totalDiff !== 0
                                                    ? `${t('cash_is') || 'Cash is'} ${fmt(Math.abs(totalDiff), currency)} ${totalDiff < 0 ? (t('short') || 'short') : (t('over') || 'over')} ${t('across_tills') || 'across tills'}`
                                                    : (t('threshold_exceeded') || 'Threshold exceeded')}
                                            />
                                        );
                                    }
                                    return (
                                        <MobileBanner
                                            tone="good"
                                            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>}
                                            title={t('all_tills_balanced') || 'All tills balanced'}
                                            subtitle={t('no_discrepancies_today') || 'No discrepancies today'}
                                        />
                                    );
                                })()}
                            </>
                        ) : (
                            <div className="portal-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
                                <KpiCard
                                    label={t('total_sales') || 'Total Sales'}
                                    value={fmt(totalSales, currency)}
                                    sub={`${sessions.length} ${sessions.length !== 1 ? (t('sessions') || 'sessions') : (t('session') || 'session')} ${t('today') || 'today'}`}
                                />
                                <KpiCard
                                    label={t('cash_collected') || 'Cash Collected'}
                                    value={fmt(totalCash, currency)}
                                    sub={`${cashPct}% ${t('of_total') || 'of total'}`}
                                />
                                <KpiCard
                                    label={t('total_expenses') || 'Total Expenses'}
                                    value={fmt(totalExpenses, currency)}
                                    sub={`${t('from') || 'From'} ${closedSessions.length} ${closedSessions.length !== 1 ? (t('tills') || 'tills') : (t('till') || 'till')}`}
                                />
                                <KpiCard
                                    label={t('discrepancies') || 'Discrepancies'}
                                    value={flaggedCount > 0 ? `${flaggedCount} ${t('flagged') || 'Flagged'}` : (t('none') || 'None')}
                                    alert={flaggedCount > 0}
                                    sub={flaggedCount > 0 ? (t('threshold_exceeded') || 'Threshold exceeded') : (t('all_within_threshold') || 'All within threshold')}
                                />
                            </div>
                        )}

                        {viewMode === 'per_till' ? (
                            <>
                                {sessions.length === 0 ? (
                                    <div style={{
                                        textAlign: 'center', padding: '48px 20px', color: C.muted,
                                        fontSize: 14, fontFamily: FONT,
                                    }}>
                                        <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>&#128203;</div>
                                        <div style={{ fontWeight: 700, color: C.text, marginBottom: 4 }}>
                                            {t('no_sessions_for_date') || 'No sessions for this date'}
                                        </div>
                                        <div>{t('no_sessions_description') || 'There are no cashier sessions recorded for the selected date.'}</div>
                                    </div>
                                ) : sessionsBySalesDate.length === 1 ? (
                                    /* Single sales_date — flat Active/Closed layout */
                                    <>
                                        {activeSessions.length > 0 && (
                                            <div style={{ marginBottom: 28 }}>
                                                <SectionHeader
                                                    title={t('active_sessions') || 'Active Sessions'}
                                                    badge={`${activeSessions.length} ${t('active') || 'Active'}`}
                                                    badgeType="green"
                                                />
                                                {activeSessions.map(s => (
                                                    <ActiveSessionCard
                                                        key={s.session_id}
                                                        session={s}
                                                        currency={currency}
                                                        onForceClose={(session) => setShowForceClose(session)}
                                                        t={t}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        {closedSessions.length > 0 && (
                                            <div>
                                                <SectionHeader
                                                    title={t('closed_sessions') || 'Closed Sessions'}
                                                    badge={`${closedSessions.length} ${t('closed') || 'Closed'}`}
                                                    badgeType="gray"
                                                />
                                                {closedSessions.map(s => (
                                                    <ClosedSessionCard key={s.session_id} session={s} currency={currency} t={t} />
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    /* Multiple sales_dates — accordion grouped layout */
                                    sessionsBySalesDate.map(([salesDate, groupSessions]) => {
                                        const isExpanded = expandedGroups[salesDate] !== false;
                                        const activeInGroup = groupSessions.filter(s => s.status === 'open');
                                        const closedInGroup = groupSessions.filter(s => s.status !== 'open');
                                        const hasActive = activeInGroup.length > 0;

                                        return (
                                            <div key={salesDate} style={{ marginBottom: 20 }}>
                                                {/* Sales Date Accordion Header */}
                                                <div
                                                    onClick={() => toggleGroup(salesDate)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                        padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                                                        background: hasActive ? C.greenLight : '#f1f5f9',
                                                        border: `1px solid ${hasActive ? C.greenBorder : C.border}`,
                                                        marginBottom: isExpanded ? 12 : 0,
                                                        transition: 'all 0.15s',
                                                        fontFamily: FONT,
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        <div style={{
                                                            width: 8, height: 8, borderRadius: '50%',
                                                            background: hasActive ? C.green : C.muted,
                                                        }} />
                                                        <span style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: 'monospace' }}>
                                                            {salesDate}
                                                        </span>
                                                        <span style={{ fontSize: 12, color: C.muted }}>
                                                            {groupSessions.length} {groupSessions.length === 1 ? (t('session') || 'session') : (t('sessions') || 'sessions')}
                                                        </span>
                                                        {hasActive && (
                                                            <span style={{
                                                                fontSize: 10, fontWeight: 700, color: C.green,
                                                                background: '#fff', padding: '2px 8px', borderRadius: 4,
                                                                textTransform: 'uppercase', letterSpacing: 0.5,
                                                            }}>
                                                                {activeInGroup.length} {t('active') || 'Active'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24"
                                                        stroke={C.muted} strokeWidth={2.5}
                                                        style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </div>

                                                {/* Accordion Body */}
                                                {isExpanded && (
                                                    <div>
                                                        {activeInGroup.map(s => (
                                                            <ActiveSessionCard
                                                                key={s.session_id}
                                                                session={s}
                                                                currency={currency}
                                                                onForceClose={(session) => setShowForceClose(session)}
                                                                t={t}
                                                            />
                                                        ))}
                                                        {closedInGroup.map(s => (
                                                            <ClosedSessionCard key={s.session_id} session={s} currency={currency} t={t} />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </>
                        ) : (
                            /* Store Summary view */
                            <div style={{ marginTop: 4 }}>
                                <SectionHeader title={t('store_summary') || 'Store Summary'} />
                                {storeSummary ? (
                                    <StoreSummaryCard summary={storeSummary} date={selectedDate} currency={currency} t={t} />
                                ) : (
                                    <EmptyState t={t} />
                                )}
                            </div>
                        )}

                        {/* Sales half — same business day as the cash half above. */}
                        <EodSalesSections
                            report={report}
                            currency={currency}
                            t={t}
                            isMobile={isMobile}
                        />
                    </>
                )}
            </div>

            {/* Force Close Modal */}
            <Modal
                open={!!showForceClose}
                onClose={() => { setShowForceClose(null); setForceCloseReason(''); setForceCloseNotCounted(false); }}
                title={t('force_close_session') || 'Force Close Session'}
            >
                <div style={{ fontFamily: FONT }}>
                    {showForceClose && (
                        <div style={{ marginBottom: 16, fontSize: 13, color: C.muted }}>
                            {t('closing_session_for') || 'Closing session'} <strong>{showForceClose.session_id}</strong> {t('for') || 'for'} <strong>{showForceClose.employee_name}</strong>.
                            {' '}{t('session_marked_force_closed') || 'This session will be marked as force-closed.'}
                        </div>
                    )}
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 6 }}>
                            {t('reason') || 'Reason'}
                        </label>
                        <textarea
                            value={forceCloseReason}
                            onChange={(e) => setForceCloseReason(e.target.value)}
                            placeholder={t('force_close_reason_placeholder') || 'Why is this session being force-closed?'}
                            style={{
                                width: '100%', minHeight: 80, padding: '10px 12px', borderRadius: 8,
                                border: `1px solid ${C.border}`, fontSize: 13, fontFamily: FONT,
                                resize: 'vertical', outline: 'none',
                            }}
                        />
                    </div>
                    <label style={{
                        display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                        color: C.text, cursor: 'pointer', marginBottom: 20,
                    }}>
                        <input
                            type="checkbox"
                            checked={forceCloseNotCounted}
                            onChange={(e) => setForceCloseNotCounted(e.target.checked)}
                            style={{ width: 16, height: 16, accentColor: C.green }}
                        />
                        {t('cash_not_counted') || 'Cash was not counted'}
                    </label>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <button
                            onClick={() => { setShowForceClose(null); setForceCloseReason(''); setForceCloseNotCounted(false); }}
                            style={{
                                padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                                cursor: 'pointer', background: C.white, border: `1px solid ${C.border}`,
                                color: '#475569', fontFamily: FONT,
                            }}
                        >{t('cancel') || 'Cancel'}</button>
                        <button
                            onClick={handleForceClose}
                            disabled={forceClosing}
                            style={{
                                padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                                cursor: forceClosing ? 'not-allowed' : 'pointer',
                                background: C.red, color: C.white, border: 'none',
                                fontFamily: FONT, opacity: forceClosing ? 0.6 : 1,
                            }}
                        >{forceClosing ? (t('closing') || 'Closing...') : (t('confirm_force_close') || 'Confirm Force Close')}</button>
                    </div>
                </div>
            </Modal>
        </PortalLayout>
    );
};

export default PortalEodPage;
